import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useAuth, usePointsData } from '../../hooks/useApi';
import { theme, animations } from '../../styles/theme';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background: ${theme.gradients.background};
  position: relative;
  overflow-x: hidden;
  padding: 2rem 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 80%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 40% 40%, rgba(0, 0, 0, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: ${theme.colors.backgroundStars};
    background-size: 25% 25%;
    pointer-events: none;
    background-repeat: repeat;
    animation: ${animations.sparkle} 4s ease-in-out infinite;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1000px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
  box-sizing: border-box;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const PointsHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
  animation: ${fadeIn} 0.6s ease-out;

  h1 {
    font-size: clamp(2rem, 5vw, 3.5rem);
    font-weight: ${theme.typography.fontWeights.bold};
    background: ${theme.gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
    text-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
  }
`;

const PointsDisplay = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  padding: 40px;
  border-radius: ${theme.borderRadius.large};
  text-align: center;
  margin-bottom: 40px;
  box-shadow: ${theme.shadows.card};
  backdrop-filter: blur(10px);
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const PointsValue = styled.div`
  font-size: clamp(3rem, 8vw, 5rem);
  font-weight: 800;
  background: ${theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin-bottom: 10px;
`;

const PointsLabel = styled.div`
  font-size: clamp(1rem, 2vw, 1.2rem);
  color: ${theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 600;
`;

const HistoryContainer = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large};
  padding: 30px;
  box-shadow: ${theme.shadows.card};
  backdrop-filter: blur(10px);
  animation: ${fadeIn} 1s ease-out;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const HistoryTitle = styled.h2`
  margin: 0 0 25px 0;
  color: ${theme.colors.textPrimary};
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &::before {
    content: '📜';
  }
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const HistoryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 4px solid ${props => props.type === 'earned' ? '#51cf66' : '#ff6b6b'};
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateX(5px);
    border-color: rgba(255, 255, 255, 0.1);
  }
`;

const HistoryDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const HistoryAction = styled.span`
  font-weight: 600;
  color: ${theme.colors.textPrimary};
  font-size: 1.1rem;
`;

const HistoryDate = styled.span`
  font-size: 0.85rem;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HistoryPoints = styled.span`
  font-weight: 800;
  font-size: 1.2rem;
  color: ${props => props.type === 'earned' ? '#51cf66' : '#ff6b6b'};
  text-shadow: 0 0 10px ${props => props.type === 'earned' ? 'rgba(81, 207, 102, 0.3)' : 'rgba(255, 107, 107, 0.3)'};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 50px;
  font-size: 1.2rem;
  color: ${theme.colors.textSecondary};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  background: rgba(220, 53, 69, 0.1);
  color: #ff6b6b;
  border: 1px solid rgba(220, 53, 69, 0.2);
  border-radius: 8px;
  margin: 20px 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 50px;
  color: ${theme.colors.textMuted};
  font-style: italic;
`;

const RefreshButton = styled.button`
  background: ${theme.gradients.primary};
  color: #000;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 20px;
  font-weight: 600;
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const ClickIndicator = styled.span`
  color: ${theme.colors.primary};
  font-size: 0.75rem;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Modal = styled.div`
  position: fixed;
  
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(5px);
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalContent = styled.div`
  background: ${theme.colors.cardBackgroundThick};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large};
  padding: 30px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  color: ${theme.colors.textPrimary};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
  padding-bottom: 5px;
  border-bottom: 1px solid ${theme.colors.cardBorder};
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: ${theme.colors.textPrimary};
  font-size: 1.5rem;
  background: ${theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
  
  &:hover {
    color: #fff;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: ${theme.colors.textSecondary};
  min-width: 120px;
`;

const DetailValue = styled.span`
  color: ${theme.colors.textPrimary};
  flex: 1;
  text-align: right;
  
  &.points {
    font-weight: 800;
    color: ${props => props.type === 'earned' ? '#51cf66' : '#ff6b6b'};
    font-size: 1.2rem;
  }
`;

const RewardBreakdown = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: 12px;
  padding: 20px;
  margin-top: 15px;
`;

const RewardTitle = styled.div`
  font-weight: bold;
  color: ${theme.colors.textPrimary};
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
`;

const RewardItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 0.95rem;
  
  &.base {
    color: ${theme.colors.textSecondary};
  }
  
  &.bonus {
    color: #ffd700;
    text-shadow: 0 0 10px rgba(255, 215, 0, 0.2);
  }
  
  &.info {
    color: ${theme.colors.textMuted};
    font-style: italic;
    background: rgba(255, 255, 255, 0.05);
    padding: 8px 12px;
    border-radius: 6px;
    margin-bottom: 10px;
  }
  
  &.total {
    color: #51cf66;
    font-weight: bold;
    font-size: 1.1rem;
    padding-top: 12px;
    margin-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const RewardIcon = styled.span`
  font-size: 1rem;
  margin-right: 8px;
  display: inline-block;
  width: 20px;
  text-align: center;
`;

const PointsSystem = ({ onNavigate }) => {
  const { user, token, isAuthenticated } = useAuth();
  const { data, loading, error, refetch } = usePointsData(user, token);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  const handleTransactionClick = (transaction, index) => {
    setSelectedTransaction({ ...transaction, originalIndex: index });
    setShowModal(true);
  };
  
  const closeModal = () => {
    setShowModal(false);
    setSelectedTransaction(null);
  };
  
  const analyzeRewardBreakdown = (transaction) => {
    if (transaction.type !== 'earned') return null;
    
    const { points, action, metadata } = transaction;
    
    console.log('Analyzing transaction:', { points, action, metadata }); // Debug log
    
    // Check if it's a barcode scan transaction with proper metadata
    if (action && action.includes('barcode') && metadata) {
      const barcode = metadata.barcode || '';
      const rewardType = metadata.rewardType || 'standard';
      const rewardValue = metadata.rewardValue || 0;
      const codeValue = metadata.codeValue || 0;
      const scanId = metadata.scanId || 'N/A';
      const timestamp = metadata.timestamp || transaction.date;
      
      return {
        barcode,
        rewardValue,
        codeValue,
        rewardType,
        scanId,
        timestamp,
        totalPoints: points,
        hasMetadata: true
      };
    } else if (action && action.includes('redemption')) {
      return {
        action: 'Gift Card Purchase',
        totalPoints: Math.abs(points),
        hasMetadata: false,
        isRedemption: true
      };
    } else {
      return {
        action: 'Points Award',
        totalPoints: points,
        hasMetadata: false,
        isGeneric: true
      };
    }
  };

  // Show loading if auth is still loading
  if (!isAuthenticated) {
    return (
      <PageContainer>
        <LoadingMessage>Please log in to view your points...</LoadingMessage>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingMessage>Loading your points...</LoadingMessage>
      </PageContainer>
    );
  }

  if (error) {
    console.error('Points page error:', error);
    return (
      <PageContainer>
        <ErrorMessage>
          {error.includes('500') || error.includes('Server Error') 
            ? 'Server error occurred. Please try again later.' 
            : `Error loading points: ${error}`}
          <br />
          <RefreshButton onClick={refetch}>Try Again</RefreshButton>
        </ErrorMessage>
      </PageContainer>
    );
  }

  if (!data) {
    return (
      <PageContainer>
        <EmptyState>
          No points data available
          <br />
          <RefreshButton onClick={refetch}>Refresh</RefreshButton>
        </EmptyState>
      </PageContainer>
    );
  }

  const { userData, history } = data;
  
  // Handle case where userData might be missing
  const userPoints = userData?.points || user?.points || 0;

  return (
    <PageContainer>
      <ContentWrapper>
        <PointsHeader>
          <h1>My Balance</h1>
        </PointsHeader>

        <PointsDisplay>
          <PointsValue>{userPoints.toLocaleString()} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '18px', height: '18px', verticalAlign: 'text-bottom' }} /></PointsValue>
          <PointsLabel>Total Balance</PointsLabel>
        </PointsDisplay>

        <HistoryContainer>
          <HistoryTitle>Balance History</HistoryTitle>
          {history && history.length > 0 ? (
            <HistoryList>
              {history.map((item, index) => (
                <HistoryItem 
                  key={index} 
                  type={item.type}
                  onClick={() => handleTransactionClick(item, index)}
                  title="Click for details"
                >
                  <HistoryDetails>
                    <HistoryAction>{item.action}</HistoryAction>
                    <HistoryDate>
                      {new Date(item.date).toLocaleDateString()}
                      <ClickIndicator>View Details →</ClickIndicator>
                    </HistoryDate>
                  </HistoryDetails>
                  <HistoryPoints type={item.type}>
                    {item.type === 'earned' ? '+' : '-'}{item.points.toLocaleString()} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '14px', height: '14px', verticalAlign: 'text-bottom' }} />
                  </HistoryPoints>
                </HistoryItem>
              ))}
            </HistoryList>
          ) : (
            <EmptyState>No balance history available yet.</EmptyState>
          )}
          <div style={{ textAlign: 'center' }}>
            <RefreshButton onClick={refetch}>Refresh Data</RefreshButton>
          </div>
        </HistoryContainer>
        
        {showModal && selectedTransaction && (
          <Modal onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Transaction Details</ModalTitle>
                <CloseButton onClick={closeModal}>&times;</CloseButton>
              </ModalHeader>
              
              <DetailRow>
                <DetailLabel>Type:</DetailLabel>
                <DetailValue className="points" type={selectedTransaction.type}>
                  {selectedTransaction.type === 'earned' ? '🟢 Earned' : '🔴 Spent'}
                </DetailValue>
              </DetailRow>
              
              <DetailRow>
                <DetailLabel>Amount:</DetailLabel>
                <DetailValue className="points" type={selectedTransaction.type}>
                  {selectedTransaction.type === 'earned' ? '+' : '-'}{selectedTransaction.points.toLocaleString()} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '14px', height: '14px', verticalAlign: 'text-bottom' }} />
                </DetailValue>
              </DetailRow>
              
              {analyzeRewardBreakdown(selectedTransaction) && (() => {
                const breakdown = analyzeRewardBreakdown(selectedTransaction);
                
                if (breakdown.hasMetadata) {
                  return (
                    <>
                      <DetailRow>
                        <DetailLabel>Code:</DetailLabel>
                        <DetailValue style={{
                          fontFamily: 'monospace',
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          color: '#007bff',
                          background: 'rgba(0, 123, 255, 0.1)',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid rgba(0, 123, 255, 0.2)'
                        }}>
                          🏷️ {breakdown.barcode}
                        </DetailValue>
                      </DetailRow>
                      
                      <DetailRow>
                        {/* <DetailLabel>Reward Details:</DetailLabel> */}
                        <DetailValue>
                          <RewardBreakdown>
                            <RewardTitle>
                              <span>🎯</span>
                              Calculation Breakdown
                            </RewardTitle>
                            
                            <RewardItem className="info">
                              <span>Type: <strong>{breakdown.rewardType}</strong></span>
                            </RewardItem>
                            
                            <RewardItem className="base">
                              <span>
                                <RewardIcon>📱</RewardIcon>
                                Base Reward
                              </span>
                              <span>+100</span>
                            </RewardItem>
                            
                            {breakdown.barcode.replace(/[^a-zA-Z0-9]/g, '').length >= 12 && (
                              <RewardItem className="bonus">
                                <span>
                                  <RewardIcon>📏</RewardIcon>
                                  Long Code Bonus
                                </span>
                                <span>+500</span>
                              </RewardItem>
                            )}
                            
                            {/\d{4,}/.test(breakdown.barcode) && (
                              <RewardItem className="bonus">
                                <span>
                                  <RewardIcon>🔢</RewardIcon>
                                  Numeric Bonus
                                </span>
                                <span>+500</span>
                              </RewardItem>
                            )}
                            
                            {/[A-Z]{3,}/.test(breakdown.barcode.toUpperCase()) && (
                              <RewardItem className="bonus">
                                <span>
                                  <RewardIcon>🔤</RewardIcon>
                                  Letter Bonus
                                </span>
                                <span>+500</span>
                              </RewardItem>
                            )}
                            
                            {(() => {
                              let baseCalculated = 100;
                              const cleanCode = breakdown.barcode.replace(/[^a-zA-Z0-9]/g, '');
                              if (cleanCode.length >= 12) baseCalculated += 500;
                              if (/\d{4,}/.test(breakdown.barcode)) baseCalculated += 500;
                              if (/[A-Z]{3,}/.test(breakdown.barcode.toUpperCase())) baseCalculated += 500;
                              
                              const luckyBonus = breakdown.rewardValue - baseCalculated;
                              
                              return luckyBonus > 0 ? (
                                <RewardItem className="bonus">
                                  <span>
                                    <RewardIcon>🍀</RewardIcon>
                                    Lucky Bonus
                                  </span>
                                  <span>+{luckyBonus}</span>
                                </RewardItem>
                              ) : null;
                            })()}
                            
                            <RewardItem className="base">
                              <span>
                                <RewardIcon>🏷️</RewardIcon>
                                Code Value
                              </span>
                              <span>+{breakdown.codeValue}</span>
                            </RewardItem>
                            
                            <RewardItem className="total">
                              <span>
                                <RewardIcon>🏆</RewardIcon>
                                Total Earned
                              </span>
                              <span>+{breakdown.codeValue + breakdown.rewardValue}</span>
                            </RewardItem>
                          </RewardBreakdown>
                        </DetailValue>
                      </DetailRow>
                    </>
                  );
                } else {
                  return (
                    <DetailRow>
                      <DetailLabel>Action:</DetailLabel>
                      <DetailValue>{breakdown.action}</DetailValue>
                    </DetailRow>
                  );
                }
              })()}
              
              <DetailRow>
                <DetailLabel>Description:</DetailLabel>
                <DetailValue>{selectedTransaction.action}</DetailValue>
              </DetailRow>

              <DetailRow>
                <DetailLabel>Date:</DetailLabel>
                <DetailValue>
                  {new Date(selectedTransaction.date).toLocaleString()}
                </DetailValue>
              </DetailRow>
              
              <DetailRow>
                <DetailLabel>ID:</DetailLabel>
                <DetailValue style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: theme.colors.textMuted }}>
                  #{selectedTransaction.originalIndex + 1}
                </DetailValue>
              </DetailRow>
            </ModalContent>
          </Modal>
        )}
      </ContentWrapper>
    </PageContainer>
  );
};

export default PointsSystem;